import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import {
  TrendingUp, MapPin, CircleDot, Star, Zap, Eye, GraduationCap, Award,
  User, Activity, Users, Share2, Video, MousePointerClick, Flame,
} from "lucide-react";

const PERIODS = [["7d", "7 Days"], ["30d", "30 Days"], ["90d", "90 Days"], ["1y", "1 Year"], ["all", "All Time"]];
const PROVINCES = ["", "ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const POSITIONS = ["", "RHP", "LHP", "C", "1B", "2B", "3B", "SS", "OF", "UTIL"];
const rank = (v) => (v == null ? "—" : `#${v}`);

export default function Movers() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ period: "30d", province: "", graduation_year: "", position: "", metric: "" });

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ period: f.period, limit: "8" });
    if (f.province) q.set("province", f.province);
    if (f.graduation_year) q.set("graduation_year", f.graduation_year);
    if (f.position) q.set("position", f.position);
    if (f.metric) q.set("metric", f.metric);
    api.get(`/movers?${q.toString()}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [f]);

  const cat = d?.categories || {};
  const mom = cat.recruiting_momentum || {};
  const years = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() + i);

  return (
    <div className="min-h-screen bg-[#F1F3F6]">
      <Header />
      {/* Hero */}
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
          <div className="flex items-center gap-2"><Flame className="w-4 h-4 text-[#E63946]" /><span className="label-overline text-white/50">Player Development & Recruiting Momentum</span></div>
          <h1 className="font-display text-5xl lg:text-7xl uppercase tracking-tighter mt-2">Movers <span className="text-[#E63946]">&</span> Risers</h1>
          <p className="text-white/60 max-w-xl mt-3 text-sm">Who's climbing the rankings, improving their metrics, and gaining recruiting attention across Canadian baseball — updated automatically.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-30 bg-white border-b border-slate-200" data-testid="movers-filters">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex flex-wrap items-center gap-2">
          <div className="flex gap-1 mr-auto flex-wrap">
            {PERIODS.map(([v, l]) => (
              <button key={v} data-testid={`movers-period-${v}`} onClick={() => setF({ ...f, period: v })}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm ${f.period === v ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
            ))}
          </div>
          <select data-testid="movers-filter-province" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5">
            {PROVINCES.map((p) => <option key={p} value={p}>{p || "All Provinces"}</option>)}
          </select>
          <select data-testid="movers-filter-grad" value={f.graduation_year} onChange={(e) => setF({ ...f, graduation_year: e.target.value })} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5">
            <option value="">All Grad Years</option>
            {years.map((y) => <option key={y} value={y}>{`Class of ${y}`}</option>)}
          </select>
          <select data-testid="movers-filter-position" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5">
            {POSITIONS.map((p) => <option key={p} value={p}>{p || "All Positions"}</option>)}
          </select>
          <select data-testid="movers-filter-metric" value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value })} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5">
            <option value="">Best Metric</option>
            {Object.entries(d?.metric_options || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-testid="movers-page">
        {loading && !d ? (
          <div className="py-20 text-center label-overline text-slate-400">Loading Movers & Risers…</div>
        ) : (
          <>
            {d?.snapshot_status === "gathering" && (
              <div className="mb-6 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-sm px-4 py-3" data-testid="movers-gathering">
                <Activity className="w-4 h-4 animate-pulse" /> Ranking & metric movers activate once CBR has 2+ days of snapshot history. Recruiting momentum below is live now.
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Cat title="National Ranking Movers" icon={TrendingUp} card={cat.national_movers}
                row={(i) => <Row key={i.id} p={i} sub={`National ${rank(i.from_rank)} → ${rank(i.to_rank)}`} right={<Up>▲ {i.delta}</Up>} />} />
              <Cat title="Provincial Movers" icon={MapPin} card={cat.provincial_movers}
                row={(i) => <Row key={i.id} p={i} sub={`${i.province || "Prov"} ${rank(i.from_rank)} → ${rank(i.to_rank)}`} right={<Up>▲ {i.delta}</Up>} />} />
              <Cat title="Position Ranking Movers" icon={CircleDot} card={cat.position_movers}
                row={(i) => <Row key={i.id} p={i} sub={`${i.primary_position || "Pos"} ${rank(i.from_rank)} → ${rank(i.to_rank)}`} right={<Up>▲ {i.delta}</Up>} />} />
              <Cat title="CBR Rating Improvements" icon={Star} card={cat.rating_improvements}
                row={(i) => <Row key={i.id} p={i} sub={`${i.from} → ${i.to}`} right={<Up>+{i.delta}</Up>} />} />
              <Cat title="Verified Metric Improvements" icon={Zap} card={cat.metric_improvements}
                row={(i) => <Row key={i.id} p={i} sub={i.label} right={<Up>+{i.delta} {i.units}</Up>} />} />
              <Cat title="Recent Commitments" icon={GraduationCap} card={cat.recent_commitments}
                row={(i) => <Row key={i.id} p={i} sub={`${i.school_name}${i.level ? ` · ${i.level}` : ""}`} />} />
              <Cat title="Recent Award Winners" icon={Award} card={cat.recent_awards}
                row={(i, idx) => <Row key={`${i.id}-${idx}`} p={i} sub={i.award_label} />} />
              <Cat title="Featured Prospects" icon={Star} card={cat.featured_prospects}
                row={(i) => <Row key={i.id} p={i} sub={[i.primary_position, i.province].filter(Boolean).join(" · ")} />} />
            </div>

            {/* Recruiting Momentum */}
            <div className="mt-10">
              <div className="flex items-center gap-2 mb-4"><Eye className="w-5 h-5 text-[#E63946]" /><h2 className="font-display text-3xl uppercase tracking-tight">Recruiting Momentum</h2></div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="movers-momentum">
                <MomCat title="Most Profile Views" icon={Eye} items={mom.most_profile_views} unit="views" />
                <MomCat title="Most Portfolio Views" icon={Activity} items={mom.most_portfolio_views} unit="opens" />
                <MomCat title="Most Watch-List Adds" icon={Users} items={mom.most_watchlist_adds} unit="adds" />
                <MomCat title="Fastest Growing Interest" icon={Flame} items={mom.fastest_growing_interest} unit="↑ views" />
                <MomCat title="Most Portfolio Shares" icon={Share2} items={mom.most_portfolio_shares} unit="shares" />
                <MomCat title="Most Video Views" icon={Video} items={mom.most_video_views} unit="views" />
                <MomCat title="Most Recruiting Link Clicks" icon={MousePointerClick} items={mom.most_link_clicks} unit="clicks" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Up({ children }) { return <span className="text-emerald-600 font-data text-sm whitespace-nowrap">{children}</span>; }

function Cat({ title, icon: Icon, card, row }) {
  const gathering = card?.status === "gathering";
  const items = card?.items || [];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`movers-cat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#E63946]" /><span className="label-overline">{title}</span></div>
      {gathering ? <Empty icon={Activity} text="Gathering snapshot data…" />
        : items.length === 0 ? <Empty text="No activity in this range yet." />
        : <div className="space-y-1">{items.map((it, idx) => row(it, idx))}</div>}
    </div>
  );
}

function MomCat({ title, icon: Icon, items, unit }) {
  const rows = items || [];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`movers-mom-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#E63946]" /><span className="label-overline">{title}</span></div>
      {rows.length === 0 ? <Empty text="No activity yet." />
        : <div className="space-y-1">{rows.map((i) => <Row key={i.id} p={i} sub={[i.primary_position, i.graduation_year].filter(Boolean).join(" · ")} right={<span className="font-data text-sm text-[#0A192F] whitespace-nowrap">{i.count} <span className="text-[10px] text-slate-400">{unit}</span></span>} />)}</div>}
    </div>
  );
}

function Row({ p, sub, right }) {
  return (
    <Link to={`/prospects/${p.slug}`} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 group">
      <div className="w-9 h-9 rounded-sm overflow-hidden bg-slate-100 shrink-0 grid place-items-center">
        {p.profile_photo_url ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0A192F] truncate group-hover:text-[#E63946]">{p.first_name} {p.last_name}</div>
        {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
      </div>
      {right || (p.cbr_overall_rating != null && <span className="font-data text-sm text-slate-500">{p.cbr_overall_rating}</span>)}
    </Link>
  );
}

function Empty({ icon: Icon, text }) {
  return <div className="flex items-center gap-2 py-6 text-slate-400 text-sm">{Icon && <Icon className="w-4 h-4" />}{text}</div>;
}
